import {useTheme} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import {formatValue} from '../utils/format';
import Highcharts, {BANDO_COLOR} from '../utils/highcharts';
import {SERIES} from '../utils/labels';
import {Comparison, Measure} from '../utils/types';

interface ComparisonColumnsProps<K extends string> {
    categories: Record<K, string>;
    data: Comparison<Record<K, Measure>>;
    suffix?: string;
    max?: number;
}

const ComparisonColumns = <K extends string>({categories, data, suffix = '', max}: ComparisonColumnsProps<K>) => {
    const theme = useTheme();
    const keys = Object.keys(categories) as K[];
    const single = keys.length === 1;

    const series = (['project', 'bando'] as const).map((scope) => ({
        type: 'column' as const,
        name: SERIES[scope],
        color: scope === 'project' ? theme.palette.primary.main : BANDO_COLOR,
        data: keys.map((key) => data[scope][key].value),
    }));

    const options: Highcharts.Options = {
        chart: {height: 260, style: {fontFamily: 'inherit'}, backgroundColor: 'transparent'},
        title: {text: undefined},
        credits: {enabled: false},
        exporting: {enabled: false},
        accessibility: {description: Object.values<string>(categories).filter(Boolean).join(', ') || undefined},
        xAxis: {categories: keys.map((key) => categories[key]), labels: {enabled: !single}, lineColor: theme.palette.divider},
        yAxis: {title: {text: undefined}, min: 0, max, labels: {format: `{value}${suffix}`}, gridLineColor: theme.palette.divider},
        legend: {enabled: true, itemStyle: {fontWeight: '400', color: theme.palette.text.secondary}},
        tooltip: {
            shared: true,
            formatter: function () {
                return [
                    single ? '' : `<b>${this.x}</b>`,
                    ...(this.points ?? []).map(
                        (point) => `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatValue(point.y ?? null, suffix)}</b>`,
                    ),
                ]
                    .filter(Boolean)
                    .join('<br/>');
            },
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                groupPadding: single ? 0.2 : 0.15,
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return formatValue(this.y ?? null, suffix);
                    },
                    style: {fontWeight: '600', textOutline: 'none', color: theme.palette.text.primary},
                },
            },
        },
        series,
        responsive: {
            rules: [
                {
                    condition: {maxWidth: 420},
                    chartOptions: {
                        xAxis: {labels: {style: {fontSize: '10px', textOverflow: 'none'}}},
                        plotOptions: {column: {groupPadding: 0.08, pointPadding: 0.04, dataLabels: {allowOverlap: true, style: {fontSize: '9px'}}}},
                    },
                },
            ],
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default ComparisonColumns;
